import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DataTable from '../../../components/ui/DataTable';
import Button from '../../../components/ui/Button';
import { PageEmpty } from '../../performance-operations/styles';
import { fetchEmployees, fmt, hasNum, signed } from '../config';

// Analiz §14 E11 — çalışan trend listesi (geçiş hücresi → çalışan listesi). Kişi KÜMESİYLE sınırlıdır (İK: kiracı; yönetici:
// doğrudan ekip, SkipLevel açıksa alt ağaç): toplulaştırma izni bireysel erişime dönüşmez. Kohort modunda yalnız ortak
// çalışanlar. Sunucu tarafı sayfalama; değerler yalnız yayımlanmış finalden.
export default function TrendEmployeesTab({ params, periods, fromBand, toBand, height }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [query, setQuery] = useState({ pageNumber: 1, pageSize: 20 });
  const [state, setState] = useState({ loading: true, error: false, rows: [], total: 0 });
  const [attempt, setAttempt] = useState(0);
  const key = JSON.stringify(params);

  useEffect(() => { setQuery((q) => ({ ...q, pageNumber: 1 })); }, [key, fromBand, toBand]);

  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: false }));
    fetchEmployees(params, { ...query, fromBand, toBand })
      .then((d) => { if (alive) setState({ loading: false, error: false, rows: d?.data || [], total: d?.totalRowCount || 0 }); })
      .catch(() => { if (alive) setState({ loading: false, error: true, rows: [], total: 0 }); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, query, fromBand, toBand, attempt]);

  const valueText = useCallback((v) => {
    if (!v || v.resultState !== 'published') return t(`performance.trends.resultState.${v?.resultState || 'none'}`, { defaultValue: '-' });
    const label = v.levelLabel ? `${v.levelLabel} (${fmt(v.value, lang)})` : fmt(v.value, lang);
    return v.band ? `${label} · ${t(`performance.trends.band.${v.band}`)}` : label;
  }, [t, lang]);

  const data = useMemo(() => state.rows.map((r) => {
    const row = {
      id: r.employeeId,
      fullName: r.fullName,
      department: r.departmentName || '-',
      title: r.titleName || '-',
      change: hasNum(r.change) ? signed(r.change, lang) : '-',
      goalChange: hasNum(r.goalChange) ? signed(r.goalChange, lang) : '-',
      transition: r.fromBand && r.toBand ? `${t(`performance.trends.band.${r.fromBand}`)} → ${t(`performance.trends.band.${r.toBand}`)}` : '-',
      roleChanged: r.roleChanged ? t('common.yes') : t('common.no'),
    };
    r.values.forEach((v, i) => {
      row[`v${i}`] = valueText(v);
      row[`g${i}`] = v.resultState === 'published' && hasNum(v.goal) ? fmt(v.goal, lang) : '-';
    });
    return row;
  }), [state.rows, t, lang, valueText]);

  const columns = useMemo(() => [
    { name: 'fullName', label: t('performance.trends.col.employee'), options: { filter: false, sort: false } },
    { name: 'department', label: t('performance.trends.col.unit'), options: { filter: false, sort: false } },
    { name: 'title', label: t('performance.trends.col.title'), options: { filter: false, sort: false } },
    ...(periods || []).flatMap((p, i) => [
      { name: `v${i}`, label: t('performance.trends.col.periodValue', { name: p.name }), options: { filter: false, sort: false } },
      { name: `g${i}`, label: t('performance.trends.col.periodGoal', { name: p.name }), options: { filter: false, sort: false } },
    ]),
    { name: 'transition', label: t('performance.trends.col.transition'), options: { filter: false, sort: false } },
    { name: 'change', label: t('performance.trends.col.change'), options: { filter: false, sort: false } },
    { name: 'goalChange', label: t('performance.trends.col.goalChange'), options: { filter: false, sort: false } },
    { name: 'roleChanged', label: t('performance.trends.col.roleChanged'), options: { filter: false, sort: false } },
  ], [periods, t]);

  const onTableChanged = useCallback((action, tableState) => {
    if (action === 'changePage' || action === 'changeRowsPerPage') {
      setQuery({ pageNumber: (tableState.page || 0) + 1, pageSize: tableState.rowsPerPage || 20 });
    }
  }, []);

  // "Yüklenemedi" ile "boş" ayrı durumdur (api.js GET'te reject etmez; sinyal res.data.status).
  if (state.error) {
    return (
      <PageEmpty data-state="error">
        <div>{t('performance.operations.panel.error')}</div>
        <Button variant="outline" onClick={() => setAttempt((a) => a + 1)} data-action="retry">{t('performance.operations.panel.retry')}</Button>
      </PageEmpty>
    );
  }

  return (
    <DataTable
      title={fromBand && toBand
        ? t('performance.trends.employeesFiltered', { from: t(`performance.trends.band.${fromBand}`), to: t(`performance.trends.band.${toBand}`) })
        : t('performance.trends.tab.employees')}
      columns={columns}
      data={data}
      totalRowCount={state.total}
      pageSize={query.pageSize}
      pageIndex={query.pageNumber - 1}
      onTableChanged={onTableChanged}
      fetching={state.loading}
      disableFilters
      tableBodyMaxHeight={height ? `${Math.max(200, height - 150)}px` : undefined}
    />
  );
}
